// Content + configuration for every ProfitMigo calculator.
// This is the single source of truth for calculator fields, engine mapping,
// scenario config, and on-page SEO content. No filler — each section is written
// to genuinely help a seller complete the task.

const RELATED_ALL = [
  "product-profit-calculator",
  "meesho-profit-calculator",
  "amazon-profit-calculator",
  "cod-profit-calculator",
  "marketplace-fee-calculator",
];

const f = (key, label, def, opts = {}) => ({ key, label, default: def, ...opts });

export const CALCULATORS = {
  "meesho-profit-calculator": {
    slug: "meesho-profit-calculator",
    path: "/ecommerce/meesho-profit-calculator",
    cluster: "ecommerce",
    calcType: "detailed",
    name: "Meesho Profit Calculator",
    seoTitle: "Meesho Profit Calculator (2026) — Real Per-Order Profit",
    seoDescription: "Free Meesho profit calculator. Enter cost, price, commission, shipping, GST and returns to see real net profit, margin, ROI and break-even per order.",
    h1: "Meesho Profit Calculator",
    valueProp:
      "Find your real per-order profit on Meesho after commission, shipping, GST and returns — not just the price minus cost.",
    feeMarketplace: "meesho",
    fields: [
      f("sellingPrice", "Selling price (customer pays)", 799, { prefix: "₹", help: "The final price the buyer pays on Meesho." }),
      f("productCost", "Product cost", 300, { prefix: "₹", help: "Your cost to buy/manufacture one unit." }),
      f("commissionPct", "Meesho commission", 5, { suffix: "%", help: "Commission for your category. Varies by category and price — verify yours." }),
      f("shippingCost", "Shipping / logistics cost", 70, { prefix: "₹", help: "Forward shipping / logistics you bear per order." }),
      f("packagingCost", "Packaging cost", 15, { prefix: "₹" }),
      f("gstPct", "GST on sale", 18, { suffix: "%", help: "Output GST assumption. Verify your product's slab." }),
      f("adCost", "Advertising cost / order", 0, { prefix: "₹" }),
      f("returnRatePct", "Return rate", 15, { suffix: "%", help: "Expected % of orders returned (reverse shipping loss)." }),
      f("otherCosts", "Other costs", 0, { prefix: "₹" }),
    ],
    scenario: { mode: "price", varKey: "sellingPrice", steps: [-150, -75, 0, 75, 150] },
    related: RELATED_ALL.filter((s) => s !== "meesho-profit-calculator"),
    content: {
      intro: [
        "Meesho sellers often look at price minus product cost and assume that gap is profit. It isn't. Commission, forward and reverse shipping, GST and — most importantly — returns quietly eat into every order.",
        "This calculator shows your true net profit per order, your margin, ROI, break-even price and the maximum you could spend on ads before an order stops being profitable.",
      ],
      howItWorks: [
        "Enter the price the customer pays, your product cost, and the costs Meesho and logistics add on top.",
        "Percentage fees (commission, GST) are charged on the selling price. Returns are modelled as an expected reverse-logistics cost across your return rate.",
        "Everything recalculates instantly as you type — no page reloads.",
      ],
      formula: [
        "Net profit = Selling price − (Product cost + Commission + Shipping + Packaging + GST + Ads + Expected return cost + Other)",
        "Commission = Selling price × Commission %",
        "Expected return cost = Return rate % × (Shipping + Packaging)",
        "Profit margin = Net profit ÷ Selling price × 100",
        "Break-even price = Fixed per-order costs ÷ (1 − percentage-fee fraction)",
      ],
      workedExample: {
        note: "Example: ₹799 price, ₹300 cost, 5% commission, ₹70 shipping, ₹15 packaging, 18% GST, 15% returns.",
        lines: [
          "Commission = 5% × ₹799 = ₹39.95",
          "GST = 18% × ₹799 = ₹143.82",
          "Expected return cost = 15% × (₹70 + ₹15) = ₹12.75",
          "Total costs = ₹300 + ₹70 + ₹15 + ₹39.95 + ₹143.82 + ₹12.75 = ₹581.52",
          "Net profit = ₹799 − ₹581.52 = ₹217.48",
        ],
      },
      assumptions: [
        "Commission and GST percentages are the values you enter — we do not hard-code Meesho's fee schedule because it changes and varies by category.",
        "Returns are modelled as an average reverse-logistics cost, not the exact loss on any single order.",
        "GST input/output offsets and TCS are simplified; treat the result as an estimate.",
      ],
      commonMistakes: [
        "Ignoring returns. On Meesho a high return rate can turn a 'profitable' order into a loss.",
        "Forgetting GST on the sale.",
        "Counting the ad spend for the whole month but the profit for a single order.",
      ],
      faq: [
        { q: "Does Meesho charge a commission?", a: "Meesho's commission depends on the category and price slab and has changed over time. Enter your actual category commission — this tool does not assume a fixed rate." },
        { q: "Why does my profit drop so much with returns?", a: "A returned order earns no revenue but still costs you reverse shipping and packaging. At a 15–25% return rate this materially lowers average profit per order." },
        { q: "Is GST really 18%?", a: "18% is only a default assumption. Verify your product's GST slab and adjust the field." },
      ],
    },
  },

  "cod-profit-calculator": {
    slug: "cod-profit-calculator",
    path: "/ecommerce/cod-profit-calculator",
    cluster: "ecommerce",
    calcType: "cod",
    name: "COD / RTO Profit Calculator",
    seoTitle: "COD & RTO Profit Calculator (India, 2026) — True Delivered Profit",
    seoDescription: "Calculate real COD profit after RTO losses. Enter orders, RTO rate, shipping, COD fees and ads to see delivered profit, RTO loss and margin.",
    h1: "COD / RTO Profit Calculator",
    valueProp:
      "See what Cash-on-Delivery orders actually earn after RTO (Return To Origin) losses — the number most Indian sellers get wrong.",
    fields: [
      f("orders", "Total orders placed", 100, { help: "Orders shipped in the period." }),
      f("sellingPrice", "Selling price per order", 699, { prefix: "₹" }),
      f("productCost", "Product cost", 280, { prefix: "₹" }),
      f("shippingPerOrder", "Forward shipping / order", 70, { prefix: "₹" }),
      f("packagingCost", "Packaging / order", 15, { prefix: "₹" }),
      f("codFeePct", "COD collection fee", 2, { suffix: "%" }),
      f("rtoRatePct", "RTO rate", 25, { suffix: "%", help: "% of COD orders returned to origin (undelivered)." }),
      f("rtoShippingCost", "RTO shipping cost / order", 120, { prefix: "₹", help: "Forward + reverse logistics lost on a returned order." }),
      f("adCostPerOrder", "Ad cost / order", 40, { prefix: "₹" }),
      f("otherPerOrder", "Other cost / order", 0, { prefix: "₹" }),
    ],
    scenario: { mode: "rto", varKey: "rtoRatePct", steps: [0, 10, 20, 30, 40, 50] },
    related: RELATED_ALL.filter((s) => s !== "cod-profit-calculator"),
    content: {
      intro: [
        "Cash-on-Delivery drives volume in India, but RTO — orders that never get delivered and come back — is where COD businesses silently lose money.",
        "On an RTO order you earn nothing but still pay forward and reverse shipping, packaging and (often) the ad cost that acquired the order. This calculator spreads those losses across your whole order batch so you see the true delivered profit.",
      ],
      howItWorks: [
        "Enter your total orders and the share that come back as RTO.",
        "Delivered orders earn revenue and carry their normal costs. RTO orders earn nothing but still cost reverse shipping, packaging and ad spend.",
        "The result shows total net profit, total RTO loss, and profit per delivered order.",
      ],
      formula: [
        "Delivered orders = Orders × (1 − RTO %)",
        "RTO orders = Orders × RTO %",
        "RTO loss = RTO orders × (RTO shipping + Packaging + Ad cost)",
        "Net profit = (Delivered × (Price − delivered costs)) − RTO loss",
      ],
      workedExample: {
        note: "Example: 100 orders, ₹699 price, ₹280 cost, ₹70 shipping, 25% RTO, ₹120 RTO shipping, ₹40 ads.",
        lines: [
          "Delivered = 100 × 75% = 75 orders",
          "RTO = 25 orders",
          "RTO loss = 25 × (₹120 + ₹15 + ₹40) = ₹4,375",
          "Delivered profit ≈ 75 × (₹699 − ₹280 − ₹70 − ₹15 − ₹14 COD − ₹40 ads)",
          "Net profit = delivered profit − RTO loss",
        ],
      },
      assumptions: [
        "The product on an RTO order is assumed returned to you in sellable condition (not written off).",
        "Ad spend is treated as lost on RTO orders because the acquisition cost is already paid.",
        "COD collection fee is charged on delivered orders only.",
      ],
      commonMistakes: [
        "Calculating profit only on delivered orders and ignoring the money burned on RTO.",
        "Assuming ad spend is recovered on returned orders — it isn't.",
        "Forgetting reverse shipping is often charged on top of forward shipping.",
      ],
      faq: [
        { q: "What is RTO?", a: "RTO (Return To Origin) is a COD order that the customer refuses or that fails delivery, so the courier ships it back to you. You earn no revenue but pay logistics both ways." },
        { q: "What RTO rate is normal?", a: "It varies widely by category, price and pincode mix — often 15–40% for COD fashion. Use your own data; we never invent a number for you." },
        { q: "How do I reduce RTO losses?", a: "Lower COD RTO by confirming orders, restricting COD on risky pincodes, and improving product pages. This tool shows how much each point of RTO costs you." },
      ],
    },
  },

  "product-profit-calculator": {
    slug: "product-profit-calculator",
    path: "/ecommerce/product-profit-calculator",
    cluster: "ecommerce",
    calcType: "detailed",
    name: "Product Profit Calculator",
    seoTitle: "Product Profit Calculator — Net Profit, Margin, ROI & Break-Even",
    seoDescription: "All-in-one product profit calculator. Enter cost, price, shipping, fees, ads, discounts and returns to get net profit, margin, ROI and break-even.",
    h1: "Product Profit Calculator",
    valueProp:
      "The all-in-one profit calculator: enter every cost once and see net profit, margin, ROI, break-even price and how much you can spend on ads.",
    fields: [
      f("sellingPrice", "Selling price", 999, { prefix: "₹" }),
      f("productCost", "Product cost", 400, { prefix: "₹" }),
      f("shippingCost", "Shipping cost", 60, { prefix: "₹" }),
      f("packagingCost", "Packaging cost", 20, { prefix: "₹" }),
      f("commissionPct", "Marketplace / platform fee", 10, { suffix: "%" }),
      f("paymentPct", "Payment processing fee", 2, { suffix: "%" }),
      f("gstPct", "GST / tax", 0, { suffix: "%" }),
      f("adCost", "Advertising cost", 70, { prefix: "₹" }),
      f("discount", "Discount", 0, { prefix: "₹", help: "Discount you give off the selling price." }),
      f("returnRatePct", "Return rate", 0, { suffix: "%" }),
      f("otherCosts", "Other costs", 0, { prefix: "₹" }),
    ],
    scenario: { mode: "price", varKey: "sellingPrice", steps: [-200, -100, 0, 100, 200] },
    related: RELATED_ALL.filter((s) => s !== "product-profit-calculator"),
    content: {
      intro: [
        "Every seller's most important question: does this product actually make money? Price minus cost hides shipping, packaging, platform fees, payment charges, ads, discounts, returns and tax.",
        "Enter each cost once and this calculator returns your real net profit, margin, ROI, break-even price and the maximum advertising cost the product can support per order.",
      ],
      howItWorks: [
        "Fill in the costs you know. Leave anything that doesn't apply at zero.",
        "Percentage fees apply to the selling price (after discount); fixed costs are per order.",
        "Results and the pricing scenario table update live as you type.",
      ],
      formula: [
        "Revenue = Selling price − Discount",
        "Total cost = Product + Shipping + Packaging + Platform fee + Payment fee + GST + Ads + Returns + Other",
        "Net profit = Revenue − Total cost",
        "Profit margin = Net profit ÷ Revenue × 100",
        "ROI = Net profit ÷ Total cost × 100",
      ],
      workedExample: {
        note: "Example: ₹999 price, ₹400 cost, ₹60 shipping, 10% platform fee, ₹70 ads.",
        lines: [
          "Platform fee = 10% × ₹999 = ₹99.90",
          "Payment fee = 2% × ₹999 = ₹19.98",
          "Total cost = ₹400 + ₹60 + ₹20 + ₹99.90 + ₹19.98 + ₹70 = ₹669.88",
          "Net profit = ₹999 − ₹669.88 = ₹329.12",
          "Margin = ₹329.12 ÷ ₹999 = 32.9%",
        ],
      },
      assumptions: [
        "The calculator assumes the costs you enter are complete — hidden costs you don't enter won't appear.",
        "Percentage fees are charged on the discounted selling price.",
        "Tax treatment is simplified; verify GST input credits for your business.",
      ],
      commonMistakes: [
        "Leaving out packaging, payment gateway or return costs.",
        "Using MRP instead of the actual price the customer pays after discount.",
        "Treating gross profit (price − cost) as take-home profit.",
      ],
      faq: [
        { q: "What's the difference between margin and markup?", a: "Margin is profit as a % of selling price; markup is profit as a % of cost. A 50% markup on ₹400 gives ₹600, which is a 33% margin." },
        { q: "What is 'maximum ad cost'?", a: "It's the most you could spend on advertising per order before net profit hits zero — a quick ceiling for your target ACOS/CPA." },
        { q: "Is this only for e-commerce?", a: "No. Freelancers and small businesses can use it for any priced product or service by mapping their costs to the fields." },
      ],
    },
  },

  "amazon-profit-calculator": {
    slug: "amazon-profit-calculator",
    path: "/ecommerce/amazon-profit-calculator",
    cluster: "ecommerce",
    calcType: "detailed",
    name: "Amazon Profit Calculator",
    seoTitle: "Amazon Seller Profit Calculator India (2026) — Fees & Profit",
    seoDescription: "Free Amazon India seller profit calculator. Enter price, cost, referral fee, closing fee, shipping, ads and GST to estimate net profit and margin.",
    h1: "Amazon Seller Profit Calculator (India)",
    valueProp:
      "Estimate your real profit as an Amazon India seller after referral fee, closing fee, shipping, ads, returns and GST.",
    feeMarketplace: "amazon",
    fields: [
      f("sellingPrice", "Selling price", 999, { prefix: "₹" }),
      f("productCost", "Product cost", 400, { prefix: "₹" }),
      f("commissionPct", "Referral fee", 15, { suffix: "%", help: "Amazon referral fee for your category — verify current rate." }),
      f("fixedFee", "Closing fee", 30, { prefix: "₹", help: "Fixed closing fee per unit." }),
      f("shippingCost", "Shipping / weight-handling fee", 70, { prefix: "₹" }),
      f("packagingCost", "Packaging cost", 15, { prefix: "₹" }),
      f("gstPct", "GST", 18, { suffix: "%" }),
      f("adCost", "Advertising (PPC) cost", 50, { prefix: "₹" }),
      f("returnRatePct", "Return rate", 5, { suffix: "%" }),
      f("otherCosts", "Other costs (storage etc.)", 0, { prefix: "₹" }),
    ],
    scenario: { mode: "price", varKey: "sellingPrice", steps: [-200, -100, 0, 100, 200] },
    related: RELATED_ALL.filter((s) => s !== "amazon-profit-calculator"),
    content: {
      intro: [
        "Amazon's fees stack up quickly: a referral fee on the price, a fixed closing fee, weight-handling/shipping, plus your PPC ad spend, returns and GST.",
        "This calculator estimates your net profit per unit as an Amazon India seller so you can price with confidence and know your break-even.",
      ],
      howItWorks: [
        "Enter your selling price and cost, then Amazon's referral %, closing fee and shipping/weight-handling fee for your category.",
        "Add PPC spend, expected returns and GST. Results update instantly.",
        "Fee percentages are yours to enter — Amazon's schedule changes and depends on category.",
      ],
      formula: [
        "Referral fee = Referral % × Selling price",
        "Total cost = Product + Referral + Closing + Shipping + Packaging + GST + Ads + Returns + Other",
        "Net profit = Selling price − Total cost",
        "Break-even price = Fixed per-unit costs ÷ (1 − percentage-fee fraction)",
      ],
      workedExample: {
        note: "Example: ₹999 price, ₹400 cost, 15% referral, ₹30 closing, ₹70 shipping, ₹50 ads, 18% GST.",
        lines: [
          "Referral = 15% × ₹999 = ₹149.85",
          "GST = 18% × ₹999 = ₹179.82",
          "Total cost = ₹400 + ₹149.85 + ₹30 + ₹70 + ₹15 + ₹179.82 + ₹50 = ₹894.67",
          "Net profit = ₹999 − ₹894.67 = ₹104.33",
        ],
      },
      assumptions: [
        "Referral fee, closing fee and shipping fee are the values you enter — we never hard-code Amazon's current fee schedule because it changes.",
        "FBA storage and long-term fees, if any, go into 'Other costs'.",
        "GST treatment is simplified; verify input tax credit for your business.",
      ],
      commonMistakes: [
        "Using the wrong category referral %.",
        "Forgetting the fixed closing fee and weight-handling fee.",
        "Ignoring PPC spend when calculating true per-unit profit.",
      ],
      faq: [
        { q: "What fees does Amazon India charge sellers?", a: "Typically a referral fee (a % of sale price), a fixed closing fee, and a weight-handling/shipping fee, which vary by category, price and fulfilment method. Enter your actual values." },
        { q: "Does this include FBA fees?", a: "Enter FBA weight-handling under 'shipping' and storage/other under 'Other costs'. We keep it configurable rather than assuming a fixed FBA rate." },
        { q: "Are these Amazon's official fees?", a: "No. All fee fields are seller-entered estimates. Always confirm against your current Amazon fee schedule." },
      ],
    },
  },

  "marketplace-fee-calculator": {
    slug: "marketplace-fee-calculator",
    path: "/ecommerce/marketplace-fee-calculator",
    cluster: "ecommerce",
    calcType: "detailed",
    name: "Marketplace Fee Calculator",
    seoTitle: "Marketplace Fee Calculator (India) — Amazon, Flipkart, Meesho",
    seoDescription: "Estimate marketplace fees and net payout for Indian sellers. Enter price, commission, shipping, GST and fixed fees to see fees, payout and profit.",
    h1: "Marketplace Fee Calculator",
    valueProp:
      "Estimate total marketplace fees and your net payout on any Indian platform — enter the fee structure and see exactly what lands in your account.",
    feeMarketplace: "flipkart",
    isFeeFocus: true,
    fields: [
      f("sellingPrice", "Selling price", 999, { prefix: "₹" }),
      f("productCost", "Product cost", 400, { prefix: "₹" }),
      f("commissionPct", "Commission", 15, { suffix: "%" }),
      f("fixedFee", "Fixed / collection fee", 30, { prefix: "₹" }),
      f("shippingCost", "Shipping fee", 70, { prefix: "₹" }),
      f("gstPct", "GST on fees / sale", 18, { suffix: "%" }),
      f("packagingCost", "Packaging cost", 15, { prefix: "₹" }),
      f("returnRatePct", "Return rate", 0, { suffix: "%" }),
    ],
    scenario: { mode: "price", varKey: "sellingPrice", steps: [-200, -100, 0, 100, 200] },
    related: RELATED_ALL.filter((s) => s !== "marketplace-fee-calculator"),
    content: {
      intro: [
        "Every Indian marketplace — Amazon, Flipkart, Meesho — charges a different mix of commission, fixed/collection fees, shipping and GST. This calculator turns any fee structure into a clear net payout and profit.",
        "Enter the fees for the platform you sell on and see total fees, what actually settles into your account, and your resulting margin.",
      ],
      howItWorks: [
        "Enter the price, your cost, and each fee the platform charges.",
        "The tool sums the fees, subtracts them from the sale to show your net payout, then subtracts your product and logistics costs for net profit.",
        "Switch the numbers to compare platforms side by side.",
      ],
      formula: [
        "Total fees = Commission + Fixed fee + Shipping fee + GST on fees",
        "Net payout = Selling price − Total marketplace fees",
        "Net profit = Net payout − Product cost − Packaging − Returns",
      ],
      workedExample: {
        note: "Example: ₹999 price, 15% commission, ₹30 fixed fee, ₹70 shipping, 18% GST.",
        lines: [
          "Commission = 15% × ₹999 = ₹149.85",
          "GST = 18% × ₹999 = ₹179.82",
          "Total fees = ₹149.85 + ₹30 + ₹70 + ₹179.82 = ₹429.67",
          "Net payout = ₹999 − ₹429.67 = ₹569.33",
        ],
      },
      assumptions: [
        "Fee percentages and fixed fees are the values you enter — no platform's schedule is hard-coded.",
        "GST handling is simplified; verify input credits and TCS for your business.",
        "Net payout is an estimate of settlement before returns.",
      ],
      commonMistakes: [
        "Comparing platforms on commission alone and ignoring fixed and shipping fees.",
        "Forgetting GST is charged on both the sale and some fees.",
        "Not accounting for return-related deductions.",
      ],
      faq: [
        { q: "Which marketplace has the lowest fees?", a: "It depends entirely on your category and price point. Enter each platform's fees here to compare net payout — we don't claim a universal winner." },
        { q: "What is net payout?", a: "Net payout (settlement) is what the marketplace actually credits to your bank after deducting its fees, before your own product and logistics costs." },
        { q: "Do these fees change?", a: "Yes — marketplace fees change over time and by category, which is why every fee is an editable input here." },
      ],
    },
  },
};

export const CALCULATOR_LIST = Object.values(CALCULATORS);

export function getCalculatorByPath(pathname) {
  return CALCULATOR_LIST.find((c) => c.path === pathname);
}
